import React, { useEffect, useState } from 'react';
import { ShieldCheck, Info, Upload, Copy, Check, QrCode, ScrollText, MapPin, Monitor, FileText, Download } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import API from '../lib/api';
import { downloadAgreementPdf } from '../lib/agreementPdf';

const Profile = ({ initialTab = 'kyc' }) => {
  const [tab, setTab] = useState(initialTab);

  return (
    <section className="px-4 md:px-10 py-6 pb-16">
      <div className="mx-auto max-w-[1240px]">
        <div className="rounded-3xl bg-[#fbf4dc] border border-emerald-900/15 shadow-2xl overflow-hidden">
          <div className="p-6 md:p-8 lg:p-10">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="font-serif text-[30px] md:text-[34px] text-emerald-950 tracking-tight">Portal Center</h2>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-800/40 bg-emerald-200/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" /> Real Estate
              </span>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <TabBtn active={tab === 'kyc'} onClick={() => setTab('kyc')}>KYC</TabBtn>
              <TabBtn active={tab === 'auth'} onClick={() => setTab('auth')}>Authentication</TabBtn>
              <TabBtn active={tab === 'addr'} onClick={() => setTab('addr')}>Delivery addresses</TabBtn>
            </div>

            <div className="mt-6">
              {tab === 'kyc' && <KYCTab />}
              {tab === 'auth' && <AuthTab />}
              {tab === 'addr' && <AddressTab />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TabBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`rounded-xl px-4 py-2 text-sm font-medium transition ${active ? 'bg-emerald-950 text-emerald-50 shadow-sm' : 'bg-emerald-100/60 text-emerald-900 hover:bg-emerald-100'}`}
  >
    {children}
  </button>
);

const LightField = ({ label, children, required }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/80 mb-2">
      {label}{required && <span className="text-emerald-700 ml-0.5">*</span>}
    </span>
    {children}
  </label>
);

/* -------- KYC TAB -------- */
const KYCTab = () => {
  const { toast } = useToast();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    firstName: '', lastName: '', dob: '', docNo: '', mobile: '',
    addr1: '', addr2: '', state: '', zip: '', country: '',
  });
  const [docFront, setDocFront] = useState(null);  // base64 data URL
  const [docBack, setDocBack] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [kycStatus, setKycStatus] = useState({ status: 'NOT_SUBMITTED', reason: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get('/kyc/me');
        setKycStatus({ status: data.status, reason: data.reason });
        if (data.kyc) {
          setForm({
            firstName: data.kyc.firstName || '', lastName: data.kyc.lastName || '',
            dob: data.kyc.dob || '', docNo: data.kyc.docNo || '', mobile: data.kyc.mobile || '',
            addr1: data.kyc.addr1 || '', addr2: data.kyc.addr2 || '',
            state: data.kyc.state || '', zip: data.kyc.zip || '', country: data.kyc.country || '',
          });
          setDocFront(data.kyc.doc_front || null);
          setDocBack(data.kyc.doc_back || null);
          setSelfie(data.kyc.selfie || null);
        }
      } catch {}
    })();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Rough English -> Russian transliteration for display only
  const toCyr = (s = '') => {
    const map = { a: 'а', b: 'б', c: 'к', d: 'д', e: 'е', f: 'ф', g: 'г', h: 'х', i: 'и', j: 'й', k: 'к', l: 'л', m: 'м', n: 'н', o: 'о', p: 'п', q: 'к', r: 'р', s: 'с', t: 'т', u: 'у', v: 'в', w: 'в', x: 'кс', y: 'ы', z: 'з' };
    return s.split('').map((c) => {
      const lower = c.toLowerCase();
      const cy = map[lower] || c;
      return c === lower ? cy : cy.toUpperCase();
    }).join('');
  };

  const onDrop = (e) => {
    e.preventDefault();
  };

  // Client-side resize + base64 conversion. Max input 2MB per slot.
  // After compress, we auto-shrink further until data URL is under ~1.5MB.
  const MAX_FILE_BYTES = 2 * 1024 * 1024; // 2MB per slot as requested
  const MAX_DATAURL_BYTES = 900 * 1024; // ~900KB data URL per slot → total <3MB, safely under any ingress limit

  const fileToDataURL = (file) => new Promise((resolve, reject) => {
    if (!file) return resolve(null);
    if (file.size > MAX_FILE_BYTES) {
      return reject(new Error(`File too large. Max 2 MB per slot. Selected: ${(file.size / 1024 / 1024).toFixed(2)} MB`));
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Read failed'));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Try progressively smaller dims/quality until under target size
        const attempts = [
          [1400, 0.82], [1200, 0.78], [1000, 0.72], [850, 0.68], [700, 0.62],
        ];
        for (const [maxDim, q] of attempts) {
          let { width, height } = img;
          const scale = Math.min(1, maxDim / Math.max(width, height));
          width = Math.round(width * scale);
          height = Math.round(height * scale);
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', q);
          if (dataUrl.length <= MAX_DATAURL_BYTES) return resolve(dataUrl);
        }
        // Last resort: smallest attempt
        const canvas = document.createElement('canvas');
        canvas.width = 700; canvas.height = 700;
        canvas.getContext('2d').drawImage(img, 0, 0, 700, 700);
        resolve(canvas.toDataURL('image/jpeg', 0.6));
      };
      img.onerror = () => reject(new Error('Unsupported image format'));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const handleSlotUpload = (setter) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await fileToDataURL(file);
      setter(data);
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message });
    }
    // Reset input so same file can be re-selected
    if (e.target) e.target.value = '';
  };

  const [savingDraft, setSavingDraft] = useState(false);

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      await API.post('/kyc/draft', { ...form });
      toast({
        title: 'Details saved',
        description: 'You can upload identity documents later to complete your KYC.',
        duration: 6000,
      });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Please try again.';
      toast({ title: 'Save failed', description: msg });
    } finally {
      setSavingDraft(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (kycStatus.status !== 'APPROVED' && (!docFront || !selfie)) {
      return toast({ title: 'Documents required', description: 'Please upload document front and selfie with ID.' });
    }
    try {
      const { data } = await API.post('/kyc', { ...form, doc_front: docFront, doc_back: docBack, selfie: selfie });
      const status = data?.status || 'PENDING';
      toast({
        title: 'KYC Submitted Successfully',
        description: 'Our verification team will review your documents within 24 hours.',
        duration: 8000,
      });
      setKycStatus({ status, reason: data?.reason || null });
      refresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (err.response?.status === 413 ? 'Uploaded documents exceed size limit. Please use smaller images (max 2 MB per slot).' : 'Please try again.');
      toast({ title: 'KYC submit failed', description: msg });
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-emerald-700" />
          <span className="text-emerald-900 font-semibold text-[15px]">Personal KYC Verification</span>
        </div>

        <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-100/60 p-4">
          <div className="flex items-center gap-2 text-amber-900 font-semibold text-[13px]">
            <Info size={14} /> KYC Verification Tips (Инструкции по верификации)
          </div>
          <ul className="mt-2 space-y-1.5 text-[13px] text-amber-950/90">
            <li><span className="font-semibold">English Name:</span> Your first and last name must be spelled in Latin/English characters exactly as shown in your document.</li>
            <li><span className="font-semibold">Full DOB:</span> Your date of birth must be fully visible and complete (DD/MM/YYYY).</li>
            <li><span className="font-semibold">Selfie with Document:</span> You must attach a high-quality selfie holding your ID document next to your face. Both your face and the document text must be perfectly readable.</li>
            <li><span className="font-semibold">Documents Clear &amp; Complete:</span> Avoid blur, glare, cropping, or hiding any data fields. All corners of the document must be visible.</li>
          </ul>
        </div>

        <div className="mt-5 rounded-xl border border-emerald-900/15 bg-white/50 px-4 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/70">Verification Status:</div>
          <StatusBadge status={kycStatus.status} />
        </div>
        {kycStatus.status === 'REJECTED' && kycStatus.reason && (
          <div className="mt-3 rounded-xl border border-red-500/40 bg-red-100/60 p-4 text-[13px] text-red-900">
            <div className="font-semibold mb-1">Admin rejection reason:</div>
            {kycStatus.reason}
            <div className="mt-2 text-[12.5px] text-red-800/90">You can edit your details and re-upload documents below. Once resubmitted, the verification team will re-review your KYC.</div>
          </div>
        )}

        {kycStatus.status === 'PENDING' && (
          <div className="mt-4 rounded-xl border border-sky-500/40 bg-gradient-to-br from-sky-100 via-emerald-50 to-amber-100 p-5 text-center" data-testid="kyc-submitted-banner">
            <div className="inline-flex items-center gap-2 text-emerald-900 font-bold text-[15px] mb-2">
              <ShieldCheck size={18} className="text-emerald-700" />
              KYC Submitted Successfully
            </div>
            <p className="text-[13.5px] text-emerald-950/90 leading-relaxed max-w-2xl mx-auto">
              Your KYC documents have been submitted successfully. Our verification team will review your documents and provide an update within 24 hours. Thank you for your patience and cooperation.
            </p>
            <p className="mt-3 text-[12px] text-emerald-900/60">Need to change something? Please contact support — details cannot be edited until the review is complete.</p>
          </div>
        )}

        {kycStatus.status === 'APPROVED' && (
          <div className="mt-4 rounded-xl border border-emerald-600/40 bg-emerald-100/60 p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-emerald-900 font-semibold text-[13px]">
                <FileText size={14} /> Convertible Note Agreement Available
              </div>
              <p className="mt-1 text-[12.5px] text-emerald-900/80">
                Your KYC is verified. Download your personalised, signed & sealed investor agreement (SkywayGreen.LLC, Saint Lucia).
              </p>
            </div>
            <button
              type="button"
              data-testid="download-agreement-btn"
              onClick={() => downloadAgreementPdf({ kyc: { ...form, email: user?.email }, joinDate: user?.created_at })}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-semibold px-4 py-2.5 transition shrink-0"
            >
              <Download size={14} /> Download Agreement PDF
            </button>
          </div>
        )}
      </div>

      {/* Editable KYC form + document upload — only shown when user is allowed to submit / resubmit */}
      {(kycStatus.status === 'NOT_SUBMITTED' || kycStatus.status === 'DRAFT' || kycStatus.status === 'REJECTED' || !kycStatus.status) && (
        <>
        {/* Bilingual form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700 mb-4">English Data Input</div>
          <div className="space-y-4">
            <LightField label="First Name (English)" required>
              <input value={form.firstName} onChange={set('firstName')} placeholder="e.g. John" className="portal-input" required />
            </LightField>
            <LightField label="Last Name (English)" required>
              <input value={form.lastName} onChange={set('lastName')} placeholder="e.g. Doe" className="portal-input" required />
            </LightField>
            <div className="grid grid-cols-2 gap-4">
              <LightField label="Date of Birth" required>
                <input type="date" value={form.dob} onChange={set('dob')} className="portal-input" required />
              </LightField>
              <LightField label="Document No." required>
                <input value={form.docNo} onChange={set('docNo')} placeholder="e.g. AB123456" className="portal-input" required />
              </LightField>
            </div>
            <LightField label="Mobile No" required>
              <input value={form.mobile} onChange={set('mobile')} placeholder="e.g. +447123456789" className="portal-input" required />
            </LightField>
            <LightField label="Address Line 1" required>
              <input value={form.addr1} onChange={set('addr1')} placeholder="e.g. 12 High Street" className="portal-input" required />
            </LightField>
            <LightField label="Address Line 2">
              <input value={form.addr2} onChange={set('addr2')} placeholder="e.g. Westminster" className="portal-input" />
            </LightField>
            <div className="grid grid-cols-2 gap-4">
              <LightField label="State" required>
                <input value={form.state} onChange={set('state')} placeholder="e.g. England" className="portal-input" required />
              </LightField>
              <LightField label="ZIP Code" required>
                <input value={form.zip} onChange={set('zip')} placeholder="e.g. SW1A 1AA" className="portal-input" required />
              </LightField>
            </div>
            <LightField label="Country" required>
              <input value={form.country} onChange={set('country')} placeholder="e.g. United Kingdom" className="portal-input" required />
            </LightField>
          </div>
        </div>

        <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700">Русская версия (Auto Cyrillic)</div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-950 text-emerald-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest">
              Realtime Trans
            </span>
          </div>
          <div className="space-y-4">
            <LightField label="Имя (на русском)">
              <input readOnly value={toCyr(form.firstName)} placeholder="Имя" className="portal-input" />
            </LightField>
            <LightField label="Фамилия (на русском)">
              <input readOnly value={toCyr(form.lastName)} placeholder="Фамилия" className="portal-input" />
            </LightField>
            <LightField label="Адрес, строка 1">
              <input readOnly value={toCyr(form.addr1)} placeholder="Улица, дом, кв" className="portal-input" />
            </LightField>
            <LightField label="Адрес, строка 2">
              <input readOnly value={toCyr(form.addr2)} placeholder="Дополнительный адрес" className="portal-input" />
            </LightField>
            <div className="grid grid-cols-2 gap-4">
              <LightField label="Область / Штат">
                <input readOnly value={toCyr(form.state)} placeholder="Область" className="portal-input" />
              </LightField>
              <div className="flex items-end">
                <div className="text-[11px] text-emerald-900/60 pb-3">Same zip code used on left side.</div>
              </div>
            </div>
            <LightField label="Страна">
              <input readOnly value={toCyr(form.country)} placeholder="Страна" className="portal-input" />
            </LightField>
          </div>
        </div>
      </div>

      {/* Save details as draft — lets the user come back later to upload documents */}
      {kycStatus.status !== 'APPROVED' && (
        <button
          type="button"
          data-testid="save-kyc-details-btn"
          onClick={saveDraft}
          disabled={savingDraft}
          className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3.5 uppercase tracking-widest text-sm transition inline-flex items-center justify-center gap-2"
        >
          {savingDraft ? 'Saving…' : 'Save Details · Upload Documents Later'}
        </button>
      )}

      {/* Upload — Document Slots */}
      <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
        <div className="flex items-center gap-2">
          <ScrollText size={16} className="text-emerald-700" />
          <span className="text-emerald-900 font-semibold text-[15px]">Upload Identity Documents <span className="text-emerald-700">*</span></span>
        </div>
        <p className="mt-1 text-[13px] text-emerald-900/60">These photos are shown to the SkywayGreen review team to verify your name and document ID match. <span className="font-medium text-emerald-900">Max 2 MB per slot · up to 6 MB total (3 slots).</span></p>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <DocSlot
            testId="kyc-doc-front"
            label="Document Front"
            required
            preview={docFront}
            onChange={handleSlotUpload(setDocFront)}
            onClear={() => setDocFront(null)}
          />
          <DocSlot
            testId="kyc-doc-back"
            label="Document Back"
            preview={docBack}
            onChange={handleSlotUpload(setDocBack)}
            onClear={() => setDocBack(null)}
          />
          <DocSlot
            testId="kyc-selfie"
            label="Selfie holding ID"
            required
            preview={selfie}
            onChange={handleSlotUpload(setSelfie)}
            onClear={() => setSelfie(null)}
          />
        </div>
      </div>

      <button type="submit" className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold py-3.5 uppercase tracking-widest text-sm transition">
        Submit KYC Documents &amp; Details
      </button>
      </>
      )}
    </form>
  );
};

/* -------- AUTH (2FA) TAB -------- */
const AuthTab = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [otp, setOtp] = useState('');
  const [copied, setCopied] = useState(false);

  const secretKey = 'SGW-2FA-SEC-KEY';

  const copyKey = async () => {
    try { await navigator.clipboard.writeText(secretKey); setCopied(true); toast({ title: 'Secret key copied' }); setTimeout(() => setCopied(false), 1600); } catch {}
  };

  const verify = () => {
    if (otp.length !== 6) return toast({ title: 'Enter 6-digit code' });
    toast({ title: '2FA activated', description: 'Two-Factor Authentication is now active.' });
    setOtp('');
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-emerald-700" />
          <span className="text-emerald-900 font-semibold text-[15px]">Two-Factor Authentication (2FA) Security</span>
        </div>

        <div className="mt-4 rounded-2xl border border-emerald-900/25 bg-gradient-to-b from-[#0a1e14] to-[#04160d] p-5 text-emerald-50">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/50 bg-emerald-500/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-200">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-dot" /> Security Layer Status: Simulating
          </div>
          <p className="mt-3 text-[13.5px] text-emerald-100/80 leading-relaxed">
            Secure your SkyWay Green account with an extra level of security. Copy the secret key below or scan the generated QR code into Google Authenticator or Microsoft Authenticator.
          </p>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-5 items-center">
            <div className="h-32 w-32 rounded-xl bg-white p-2 grid place-items-center">
              <QrPattern />
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-300">Secret Key</div>
              <div className="mt-1 font-mono text-emerald-300 text-lg tracking-wider">{secretKey}</div>
              <button onClick={copyKey} className="mt-2 inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 text-sm font-medium underline underline-offset-2">
                {copied ? <Check size={12} /> : <Copy size={12} />} Copy Key
              </button>
            </div>
          </div>

          <div className="mt-5 flex flex-col sm:flex-row items-stretch gap-3">
            <input
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit OTP"
              className="flex-1 rounded-xl bg-[#04160d] border border-emerald-800 text-emerald-50 placeholder:text-emerald-100/40 px-4 py-3 text-sm outline-none focus:border-emerald-500 tracking-widest text-center"
            />
            <button onClick={verify} className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold px-5 py-3 transition">
              Verify &amp; Activate 2FA
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/70 mb-3">Your Active Sessions Log</div>
        <div className="space-y-3">
          <SessionRow browser="Google Chrome (Windows)" ip="194.83.107.25" loc="India" active current />
          <SessionRow browser="Safari (iPhone 15)" ip="86.14.11.203" loc="United Kingdom" active={false} />
        </div>
      </div>
    </div>
  );
};

const SessionRow = ({ browser, ip, loc, current }) => (
  <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-900/15 bg-white/60 px-4 py-3">
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg bg-emerald-900/10 flex items-center justify-center text-emerald-800">
        <Monitor size={16} />
      </div>
      <div>
        <div className="text-[14px] font-medium text-emerald-950">{browser}</div>
        <div className="text-[12px] text-emerald-900/60">IP: {ip} · Location: {loc}</div>
      </div>
    </div>
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${current ? 'bg-emerald-500 text-emerald-950' : 'bg-emerald-200 text-emerald-900'}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-950" /> {current ? 'Active Now' : 'Signed out'}
    </span>
  </div>
);

/* -------- ADDRESS TAB -------- */
const AddressTab = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({
    fullName: user?.email || '', addr1: '', addr2: '',
    city: '', state: '', zip: '', country: '',
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    toast({ title: 'Delivery address saved', description: 'Your certificate delivery address is up to date.' });
  };

  return (
    <form onSubmit={submit}>
      <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-emerald-700" />
          <span className="text-emerald-900 font-semibold text-[15px]">Physical Delivery Address for Certificates</span>
        </div>
        <p className="mt-1 text-[13px] text-emerald-900/60">Provide your correct delivery coordinates. If you order physical, printed replicas of your co-ownership share certificates and official agreements, they will be mailed to this destination.</p>

        <div className="mt-5 space-y-4">
          <LightField label="Receiver's Full Name" required>
            <input value={form.fullName} onChange={set('fullName')} className="portal-input" required />
          </LightField>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <LightField label="Address Line 1" required>
              <input value={form.addr1} onChange={set('addr1')} placeholder="Street, Building No, Apartment No" className="portal-input" required />
            </LightField>
            <LightField label="Address Line 2 (Optional)">
              <input value={form.addr2} onChange={set('addr2')} placeholder="District, Landmarks" className="portal-input" />
            </LightField>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <LightField label="City" required><input value={form.city} onChange={set('city')} placeholder="e.g. London" className="portal-input" required /></LightField>
            <LightField label="State / Province" required><input value={form.state} onChange={set('state')} placeholder="e.g. England" className="portal-input" required /></LightField>
            <LightField label="ZIP Code" required><input value={form.zip} onChange={set('zip')} placeholder="e.g. SW1A 1AA" className="portal-input" required /></LightField>
            <LightField label="Country" required><input value={form.country} onChange={set('country')} placeholder="e.g. United Kingdom" className="portal-input" required /></LightField>
          </div>
        </div>

        <button type="submit" className="mt-6 w-full rounded-xl bg-emerald-950 hover:bg-emerald-900 text-emerald-50 font-medium py-3.5 transition">
          Save Delivery Address
        </button>
      </div>
    </form>
  );
};

const DocSlot = ({ label, required, preview, onChange, onClear, testId }) => (
  <div className="rounded-xl border-2 border-dashed border-emerald-900/25 bg-white/60 overflow-hidden">
    <label className="block cursor-pointer">
      <div className="relative aspect-[4/3] w-full bg-emerald-50/60 flex items-center justify-center">
        {preview ? (
          <img src={preview} alt={label} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="text-center text-emerald-900/60 px-3">
            <Upload size={22} className="mx-auto mb-1 text-emerald-700" />
            <div className="text-[12px] font-medium">Tap to upload</div>
            <div className="text-[10px]">Camera or Gallery · JPG · PNG · up to 2 MB</div>
          </div>
        )}
      </div>
      <input
        data-testid={testId}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onChange}
      />
    </label>
    <div className="flex items-center justify-between px-3 py-2 border-t border-emerald-900/10 bg-white/70">
      <div className="text-[12px] font-semibold text-emerald-900">
        {label} {required && <span className="text-red-600">*</span>}
      </div>
      {preview && (
        <button type="button" onClick={onClear} className="text-[11px] text-red-700 hover:text-red-900 font-medium">Remove</button>
      )}
    </div>
  </div>
);


/* Simple decorative QR */
const StatusBadge = ({ status }) => {
  const map = {
    NOT_SUBMITTED: { cls: 'border-orange-500/40 bg-orange-100 text-orange-800', dot: 'bg-orange-500', label: 'Not Submitted' },
    PENDING: { cls: 'border-amber-500/40 bg-amber-100 text-amber-800', dot: 'bg-amber-500', label: 'Pending Review' },
    APPROVED: { cls: 'border-emerald-500/40 bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500', label: 'Approved' },
    REJECTED: { cls: 'border-red-500/40 bg-red-100 text-red-800', dot: 'bg-red-500', label: 'Rejected' },
  };
  const m = map[status] || map.NOT_SUBMITTED;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${m.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} /> {m.label}
    </span>
  );
};
const QrPattern = () => (
  <svg viewBox="0 0 21 21" width="100%" height="100%" shapeRendering="crispEdges" aria-hidden="true">
    <rect width="21" height="21" fill="#ffffff" />
    {[
      '111111100101011111111','100000101110001000001','101110100001101011101',
      '101110100110101011101','101110101010101011101','100000101011101000001',
      '111111101010101111111','000000001111100000000','110101110001011011010',
      '011010011011000110001','110001100100111101010','011010010101100000101',
      '111011100011010110111','000000001010011000010','111111101110100010110',
      '100000100011101110100','101110101010111011010','101110100110110000111',
      '101110101010101101010','100000100011011010001','111111101101110110110',
    ].map((row, y) => row.split('').map((c, x) => c === '1' ? <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill="#04160d" /> : null))}
  </svg>
);

export default Profile;
